import { maskCPF, maskPhone, removeMask } from '@/utils/masks';
import { validateCPF } from '@/utils/validations';
import { isValidWhatsAppPhone } from '@/utils/sessionWhatsAppDisplay';

export type PatientFormData = {
	full_name: string;
	cpf: string;
	birth_date: string;
	gender: Patients.PatientGender | '';
	marital_status: Patients.PatientMaritalStatus | '';
	education_level: Patients.PatientEducationLevel | '';
	phone: string;
	email: string;
	guardian_full_name: string;
	emergency_contact: string;
	rg: string;
	profession: string;
	whatsapp_notifications_enabled: boolean;
};

export const INITIAL_PATIENT_FORM_DATA: PatientFormData = {
	full_name: '',
	cpf: '',
	birth_date: '',
	gender: '',
	marital_status: '',
	education_level: '',
	phone: '',
	email: '',
	guardian_full_name: '',
	emergency_contact: '',
	rg: '',
	profession: '',
	whatsapp_notifications_enabled: true,
};

const toDateInputValue = (value: string | Date | null | undefined) => {
	if (!value) return '';
	const raw = typeof value === 'string' ? value : value.toISOString();
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
	return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
};

export const patientToFormData = (
	patient: Patients.Patient,
): PatientFormData => ({
	full_name: patient.full_name,
	cpf: patient.cpf ? maskCPF(patient.cpf) : '',
	birth_date: toDateInputValue(patient.birth_date),
	gender: patient.gender ?? '',
	marital_status: patient.marital_status ?? '',
	education_level: patient.education_level ?? '',
	phone: patient.phone ? maskPhone(patient.phone) : '',
	email: patient.email ?? '',
	guardian_full_name: patient.guardian_full_name ?? '',
	emergency_contact: patient.emergency_contact ?? '',
	rg: patient.rg ?? '',
	profession: patient.profession ?? '',
	whatsapp_notifications_enabled:
		patient.whatsapp_notifications_enabled !== false,
});

export const buildPatientBodyFromForm = (
	formData: PatientFormData,
): Patients.Create => {
	const cpfDigits = removeMask(formData.cpf);
	const phoneDigits = removeMask(formData.phone);

	const body: Patients.Create = {
		full_name: formData.full_name.trim(),
	};

	if (cpfDigits) body.cpf = cpfDigits;
	if (formData.birth_date) body.birth_date = formData.birth_date;
	if (formData.gender) body.gender = formData.gender;
	if (formData.marital_status) body.marital_status = formData.marital_status;
	if (formData.education_level)
		body.education_level = formData.education_level;
	if (phoneDigits) body.phone = phoneDigits;
	if (formData.email.trim()) body.email = formData.email.trim().toLowerCase();
	if (formData.guardian_full_name.trim()) {
		body.guardian_full_name = formData.guardian_full_name.trim();
	}
	if (formData.emergency_contact.trim()) {
		body.emergency_contact = formData.emergency_contact.trim();
	}
	if (formData.rg.trim()) body.rg = formData.rg.trim();
	if (formData.profession.trim())
		body.profession = formData.profession.trim();
	body.whatsapp_notifications_enabled =
		formData.whatsapp_notifications_enabled;

	return body;
};

export const validatePatientForm = (formData: PatientFormData) => {
	const errors: Record<string, string> = {};

	if (!formData.full_name.trim()) {
		errors.full_name = 'Obrigatório';
	}

	const cpfDigits = removeMask(formData.cpf);
	if (cpfDigits && (cpfDigits.length !== 11 || !validateCPF(cpfDigits))) {
		errors.cpf = 'CPF inválido';
	}

	const phoneDigits = removeMask(formData.phone);
	if (phoneDigits && phoneDigits.length !== 11) {
		errors.phone = 'Telefone deve ter 11 dígitos';
	}

	if (
		formData.whatsapp_notifications_enabled &&
		!isValidWhatsAppPhone(phoneDigits)
	) {
		errors.phone =
			'Informe um telefone com 11 dígitos para ativar as notificações';
	}

	if (
		formData.email.trim() &&
		!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
	) {
		errors.email = 'E-mail inválido';
	}

	return errors;
};
